import { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useAuth, IDLE_TIMEOUT_MS } from '../auth/AuthContext.jsx'
import styles from './IdleTimeoutModal.module.css'

const WARNING_LEAD_MS = 60 * 1000 // show the warning this long before actual logout
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']
const ACTIVITY_THROTTLE_MS = 5000 // don't hammer localStorage on every mousemove

export default function IdleTimeoutModal() {
  const { user, logout, recordActivity } = useAuth()
  const [secondsLeft, setSecondsLeft] = useState(null) // null = warning hidden
  const lastRecordRef = useRef(0)

  const onActivity = useCallback(() => {
    const now = Date.now()
    if (now - lastRecordRef.current < ACTIVITY_THROTTLE_MS) return
    lastRecordRef.current = now
    recordActivity()
    setSecondsLeft(null)
  }, [recordActivity])

  useEffect(() => {
    if (!user) return
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }))
    return () => ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, onActivity))
  }, [user, onActivity])

  useEffect(() => {
    if (!user) return
    const id = setInterval(() => {
      const last = Number(localStorage.getItem('sd_last_activity')) || Date.now()
      const elapsed = Date.now() - last
      if (elapsed >= IDLE_TIMEOUT_MS) {
        logout()
      } else if (elapsed >= IDLE_TIMEOUT_MS - WARNING_LEAD_MS) {
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_TIMEOUT_MS - elapsed) / 1000)))
      } else {
        setSecondsLeft(null)
      }
    }, 1000)
    return () => clearInterval(id)
  }, [user, logout])

  if (secondsLeft === null) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.title}>Still there?</div>
        <p className={styles.body}>
          You&apos;ll be signed out in <strong>{secondsLeft}s</strong> due to inactivity.
        </p>
        <div className={styles.actions}>
          <button className={styles.stayBtn} onClick={onActivity}>Stay signed in</button>
          <button className={styles.signOutBtn} onClick={logout}>Sign out now</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
